import { useEffect, useId, useRef, useState, type MouseEvent } from 'react';
import { useAppConfig, useRevealAccountPassword } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { IconCheck, IconCopy, IconEye, IconEyeOff, IconPlus } from '@/components/Icons';
import { PasswordInput } from '@/components/PasswordInput';
import { copyText } from '@/lib/clipboard';
import type { Lead } from '@/lib/types';

/** Quanto tempo a senha aberta fica à vista antes de se esconder sozinha. */
const SHOWN_MS = 30_000;
const COPIED_MS = 2_000;

interface ClientAccountProps {
  lead: Lead;
  canEdit: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}

/**
 * Acesso do cliente à conta dele na casa: login e senha. A senha fica
 * mascarada e só é buscada no servidor quando alguém pede para mostrar ou
 * copiar — cada vez fica na auditoria — e some da tela sozinha.
 */
export function ClientAccount({ lead, canEdit, onSave }: ClientAccountProps) {
  const toast = useToast();
  const { data: config } = useAppConfig();
  const reveal = useRevealAccountPassword();
  const id = useId();
  // Enquanto a configuração carrega, fica ligado: o servidor recusa de qualquer jeito, se for o caso.
  const enabled = config?.credentialsEnabled !== false;

  const [shown, setShown] = useState<string | null>(null);
  const [copied, setCopied] = useState<'login' | 'password' | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);
  const copiedTimer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(hideTimer.current);
      window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  // A senha trocada ou apagada: o que estava à vista não vale mais.
  useEffect(() => {
    setShown(null);
  }, [lead.id, lead.hasAccountPassword, lead.updatedAt]);

  async function fetchPassword(): Promise<string | null> {
    try {
      return (await reveal.mutateAsync(lead.id)).password;
    } catch (caught) {
      toast.fromError(caught, 'Não foi possível abrir a senha.');
      return null;
    }
  }

  async function toggleShown() {
    window.clearTimeout(hideTimer.current);
    if (shown !== null) {
      setShown(null);
      return;
    }
    const password = await fetchPassword();
    if (password === null) return;
    setShown(password);
    hideTimer.current = window.setTimeout(() => setShown(null), SHOWN_MS);
  }

  async function copy(kind: 'login' | 'password', event: MouseEvent<HTMLButtonElement>) {
    // Antes de qualquer await: depois dele o evento já não tem alvo.
    const container = event.currentTarget.closest<HTMLElement>('[role="dialog"]') ?? undefined;
    const value = kind === 'login' ? lead.accountLogin : (shown ?? (await fetchPassword()));
    if (!value) return;
    if (!(await copyText(value, container))) {
      toast.error('Não foi possível copiar. Mostre e copie à mão.');
      return;
    }
    setCopied(kind);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(null), COPIED_MS);
  }

  async function savePassword() {
    if (!draft) return;
    await onSave({ accountPassword: draft });
    setEditing(false);
    setDraft('');
  }

  async function removePassword() {
    await onSave({ accountPassword: null });
    setConfirmingRemove(false);
  }

  const busy = reveal.isPending;

  return (
    <section className="client-account" aria-labelledby={`${id}-titulo`}>
      <div id={`${id}-titulo`} className="label" style={{ marginBottom: 10 }}>Conta do cliente na casa</div>
      <div className="grid grid2">
        <div className="field">
          <label className="field-label" htmlFor={`${id}-login`}>Login da conta</label>
          <div className="password-input">
            <input
              id={`${id}-login`}
              key={lead.accountLogin ?? ''}
              className="inp"
              defaultValue={lead.accountLogin ?? ''}
              disabled={!canEdit}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Usuário ou e-mail do cliente na casa"
              onBlur={(event) => {
                const value = event.target.value.trim();
                if (value !== (lead.accountLogin ?? '')) void onSave({ accountLogin: value });
              }}
            />
            {lead.accountLogin ? (
              <button
                type="button"
                className="icon-btn password-toggle"
                aria-label="Copiar o login da conta"
                title={copied === 'login' ? 'Copiado' : 'Copiar login'}
                onClick={(event) => void copy('login', event)}
              >
                {copied === 'login' ? <IconCheck /> : <IconCopy />}
              </button>
            ) : null}
          </div>
        </div>

        <div className="field">
          <label className="field-label" htmlFor={`${id}-senha`}>Senha da conta</label>
          {editing ? (
            <>
              <PasswordInput
                id={`${id}-senha`}
                value={draft}
                onChange={setDraft}
                autoComplete="new-password"
                autoFocus
                placeholder="A senha do cliente na casa"
              />
              <div className="password-actions">
                <button type="button" className="btn btn-sm btn-primary" disabled={!draft} onClick={() => void savePassword()}>
                  <IconCheck /> Salvar senha
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => {
                    setEditing(false);
                    setDraft('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </>
          ) : lead.hasAccountPassword ? (
            <>
              <output id={`${id}-senha`} className={`inp secret-value${shown === null ? '' : ' is-shown'}`}>
                {shown === null ? (
                  <>
                    <span aria-hidden>••••••••••</span>
                    <span className="sr-only">Senha guardada, escondida</span>
                  </>
                ) : (
                  shown
                )}
              </output>
              <div className="password-actions">
                <button type="button" className="btn btn-sm btn-ghost" disabled={busy || !enabled} onClick={() => void toggleShown()}>
                  {shown === null ? <IconEye /> : <IconEyeOff />} {shown === null ? 'Mostrar' : 'Esconder'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={busy || !enabled}
                  onClick={(event) => void copy('password', event)}
                >
                  {copied === 'password' ? <IconCheck /> : <IconCopy />} {copied === 'password' ? 'Copiada' : 'Copiar'}
                </button>
                {canEdit && enabled ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      setShown(null);
                      setConfirmingRemove(false);
                      setEditing(true);
                    }}
                  >
                    Trocar
                  </button>
                ) : null}
                {canEdit && confirmingRemove ? (
                  <>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => void removePassword()}>
                      Remover a senha
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmingRemove(false)}>
                      Manter
                    </button>
                  </>
                ) : canEdit ? (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmingRemove(true)}>
                    Remover
                  </button>
                ) : null}
                <span className="sr-only" aria-live="polite">{copied === 'password' ? 'Senha copiada.' : ''}</span>
              </div>
              {shown === null ? null : <span className="field-hint">Some sozinha em 30 segundos.</span>}
            </>
          ) : (
            <>
              <output id={`${id}-senha`} className="inp secret-value is-empty">Nenhuma senha guardada</output>
              {canEdit && enabled ? (
                <div className="password-actions">
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>
                    <IconPlus /> Guardar senha
                  </button>
                </div>
              ) : null}
            </>
          )}
          {enabled ? (
            <span className="field-hint">Guardada criptografada. Só o responsável e os administradores veem.</span>
          ) : (
            <span className="field-hint">
              Guardar senha exige a chave de criptografia (CREDENTIALS_KEY) configurada no servidor.
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
